// Adaptive daily goals (handoff v08 #22, «Οι στόχοι της ημέρας»). Every working day the CRM proposes a few concrete,
// countable goals — prospecting calls, existing-client contacts, appointments to book, overdue follow-ups to clear,
// renewals to contact — computed locally and deterministically (no AI) from:
//
//   free time      the working hours (Settings → office) minus what is already planned (planner.ts: appointments,
//                  timed tasks, the advisor's own blocks); a light day gets more ambitious goals, a full day smaller ones
//   due work       overdue follow-ups, client tasks due today, renewals in the next 30 days not contacted lately
//   call lists     numbers that can be called now (callQueue: «do not call» and booked numbers never count)
//   performance    the advisor's own pace of calls (dials per hour on calling days) and the share of each goal reached
//                  over the last 4 weeks (from the stored snapshots and the activity actually recorded)
//   preferences    which goal types are on, a minimum / maximum per type, how much of the free time to plan
//
// Each goal carries its reasons (i18n keys + numbers) so the screen says WHY. The advisor can change a number, skip a
// goal for the day, or switch a type off. One snapshot per day (schema 14 `daily_goals`, deterministic id) keeps what
// was proposed and what the advisor changed; a recomputation keeps the advisor's numbers.
//
// Progress is read ONLY from recorded activity: dials logged (call_logs), tasks completed, communications, appointments
// booked. Each activity counts once: an overdue follow-up cleared by a call counts for «overdue» and not again as a
// prospecting call; a person counts once a day — for a cleared overdue follow-up, else for a renewal, else as a client
// contact. Appointments booked are an outcome measure (a dial that books a meeting is one dial AND one booking).
// No market news is ever generated: a market line exists only when the advisor added one, with its source.
import type { Changeset, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, put, touched, type Ctx } from './cmd.ts';
import { addDays, isDay, startOfWeek, weekday, type Day } from './dates.ts';
import { stableId } from './ids.ts';
import { renewalQueue } from './business.ts';
import { callGoals, callQueue, doNotCallKeys } from './calls.ts';
import { getSetting } from './services.ts';
import { getOffice } from './setup.ts';
import { setGoalSource, type GoalOutcome } from './performance.ts';
import { busyIntervals, freeSlots, fromMin, localDayOf, localTimeOf, minutesWithin, planTypeOf, plannerItems, plannerSettings, savePlanBlock, toMin, workingWindow, type Interval, type PlanItem } from './planner.ts';
import { DAILY_GOAL_TYPES, type CallLog, type Communication, type DailyGoal, type DailyGoalSnapshot, type DailyGoalStatus, type DailyGoalType, type GoalReason, type DailyMarketNote, type Person, type Task } from './schema.ts';

// --- preferences -------------------------------------------------------------------------------------------------
export interface GoalPrefs {
  /** goal types switched on (opt-out per type) */
  enabled: Record<DailyGoalType, boolean>;
  min: Partial<Record<DailyGoalType, number>>;
  max: Partial<Record<DailyGoalType, number>>;
  /** share of the free time the goals may fill (the rest stays for admin, travel, the unexpected) */
  utilisation: number;
  /** optional AI wording of the reasons — OFF by default; the numbers never depend on it */
  aiWording: boolean;
}
export const GOAL_PREFS_KEY = 'goals.daily';
export const DEFAULT_GOAL_PREFS: GoalPrefs = {
  enabled: { overdue_followups: true, renewals: true, customer_contacts: true, prospecting_calls: true, appointments: true },
  min: {}, max: {}, utilisation: 0.6, aiWording: false,
};
/** hard limits per type (also the range of the advisor's min / max) */
export const GOAL_LIMITS: Record<DailyGoalType, number> = { overdue_followups: 50, renewals: 30, customer_contacts: 40, prospecting_calls: 150, appointments: 10 };
/** planning minutes per unit (prospecting calls use the advisor's pace) */
export const GOAL_MINUTES: Record<DailyGoalType, number> = { overdue_followups: 10, renewals: 12, customer_contacts: 8, prospecting_calls: 6, appointments: 0 };
/** the order the goals are shown in */
export const GOAL_DISPLAY_ORDER: readonly DailyGoalType[] = ['prospecting_calls', 'customer_contacts', 'appointments', 'overdue_followups', 'renewals'];

export function normalizeGoalPrefs(raw: unknown): GoalPrefs {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const en = (o.enabled && typeof o.enabled === 'object' ? o.enabled : {}) as Record<string, unknown>;
  const lim = (src: unknown) => {
    const out: Partial<Record<DailyGoalType, number>> = {};
    const s = (src && typeof src === 'object' ? src : {}) as Record<string, unknown>;
    for (const t of DAILY_GOAL_TYPES) { const n = Math.round(Number(s[t])); if (s[t] !== null && s[t] !== '' && s[t] !== undefined && Number.isFinite(n) && n >= 0 && n <= GOAL_LIMITS[t]) out[t] = n; }
    return out;
  };
  const min = lim(o.min), max = lim(o.max);
  for (const t of DAILY_GOAL_TYPES) if (min[t] != null && max[t] != null && min[t]! > max[t]!) delete min[t];
  const u = Number(o.utilisation);
  return {
    enabled: Object.fromEntries(DAILY_GOAL_TYPES.map((t) => [t, en[t] !== false])) as Record<DailyGoalType, boolean>,
    min, max, utilisation: Number.isFinite(u) && u >= 0.2 && u <= 1 ? Math.round(u * 100) / 100 : DEFAULT_GOAL_PREFS.utilisation, aiWording: o.aiWording === true,
  };
}
export const goalPrefs = (v: WorkspaceView): GoalPrefs => normalizeGoalPrefs(getSetting<unknown>(v, GOAL_PREFS_KEY, null));

// --- recorded activity -------------------------------------------------------------------------------------------
const OVERDUE_KINDS = new Set(['call', 'followup', 'task', 'review', 'document', 'renewal', 'other']);
const CONTACT_TASK_KINDS = new Set(['call', 'followup', 'review', 'appointment', 'renewal']);
const NOT_BOOKINGS = new Set(['flow', 'legacy', 'import']);
const isClient = (p: Person | undefined) => !!p && !p.archivedAt && (p.stage === 'client' || p.kind === 'client');

export interface DayActivity { dials: CallLog[]; doneTasks: Task[]; comms: Communication[]; booked: Task[] }
const emptyActivity = (): DayActivity => ({ dials: [], doneTasks: [], comms: [], booked: [] });
/** the recorded activity per local day (dials by their own day; tasks by the day they were completed / created) */
export function activityByDay(v: WorkspaceView, from: Day, to: Day): Map<Day, DayActivity> {
  const m = new Map<Day, DayActivity>();
  const at = (d: Day | null) => { if (!d || d < from || d > to) return null; let a = m.get(d); if (!a) m.set(d, a = emptyActivity()); return a; };
  for (const l of v.rows('call_logs')) at(l.day)?.dials.push(l);
  for (const t of v.rows('tasks')) {
    if (t.status === 'done' && t.completedAt) at(localDayOf(t.completedAt))?.doneTasks.push(t);
    if (t.kind === 'appointment' && t.status !== 'cancelled' && !NOT_BOOKINGS.has(t.origin ?? '')) at(localDayOf(t.createdAt))?.booked.push(t);
  }
  for (const c of v.rows('communications')) if (c.personId) at(localDayOf(c.at))?.comms.push(c);
  for (const a of m.values()) { a.dials.sort((x, y) => x.at.localeCompare(y.at) || x.id.localeCompare(y.id)); a.doneTasks.sort((x, y) => (x.completedAt ?? '').localeCompare(y.completedAt ?? '') || x.id.localeCompare(y.id)); }
  return m;
}
/** a dial that is prospecting (a prospect, or a person who is not a client) */
const prospectingDial = (v: WorkspaceView, d: CallLog) => !!d.prospectId || (!!d.personId && !isClient(v.get('persons', d.personId)));
const REACHED = new Set(['not_interested', 'callback', 'interested', 'meeting', 'do_not_call']);
/** persons contacted in the activity (client contacts only; prospecting dials and call-list tasks are left out) → their activity keys */
function personsContacted(v: WorkspaceView, a: DayActivity): Map<string, string[]> {
  const m = new Map<string, string[]>();
  const add = (pid: string | null | undefined, key: string) => { if (!pid || !isClient(v.get('persons', pid))) return; m.set(pid, [...(m.get(pid) ?? []), key]); };
  for (const t of a.doneTasks) if (CONTACT_TASK_KINDS.has(t.kind) && !t.prospectId) add(t.personId, `tasks:${t.id}`);
  for (const c of a.comms) add(c.personId, `communications:${c.id}`);
  for (const d of a.dials) if (!d.prospectId && d.personId && REACHED.has(d.outcome)) add(d.personId, `call_logs:${d.id}`);
  return m;
}

export interface GoalProgress { type: DailyGoalType; target: number; status: DailyGoalStatus; done: number; pct: number | null; units: string[] }
/** progress of a day's goals from the activity recorded that day — every activity counted once (see the header) */
export function goalProgress(v: WorkspaceView, snap: Pick<DailyGoalSnapshot, 'day' | 'goals'>, activity?: DayActivity): GoalProgress[] {
  const a = activity ?? activityByDay(v, snap.day, snap.day).get(snap.day) ?? emptyActivity();
  const goal = (t: DailyGoalType) => snap.goals.find((g) => g.type === t && g.status === 'active');
  const units = new Map<DailyGoalType, string[]>(DAILY_GOAL_TYPES.map((t) => [t, []]));
  const claimedDials = new Set<string>(), claimedPersons = new Set<string>();
  const og = goal('overdue_followups');
  if (og) {
    const refs = new Set(og.refs);
    for (const t of a.doneTasks) {
      if (!refs.has(t.id)) continue;
      units.get('overdue_followups')!.push(`tasks:${t.id}`);
      if (t.personId) claimedPersons.add(t.personId);
      if (t.prospectId) {
        // the call that cleared it (logCall completes the prospect's open call tasks at the time of the dial)
        const d = [...a.dials].reverse().find((x) => x.prospectId === t.prospectId && !claimedDials.has(x.id) && x.at <= (t.completedAt ?? '￿')) ?? a.dials.find((x) => x.prospectId === t.prospectId && !claimedDials.has(x.id));
        if (d) claimedDials.add(d.id);
      }
    }
  }
  const contacted = personsContacted(v, a);
  const rg = goal('renewals');
  if (rg) for (const pid of rg.refs) if (contacted.has(pid) && !claimedPersons.has(pid)) { claimedPersons.add(pid); units.get('renewals')!.push(`persons:${pid}`); }
  if (goal('prospecting_calls')) for (const d of a.dials) if (!claimedDials.has(d.id) && prospectingDial(v, d)) units.get('prospecting_calls')!.push(`call_logs:${d.id}`);
  if (goal('customer_contacts')) for (const pid of contacted.keys()) if (!claimedPersons.has(pid)) { claimedPersons.add(pid); units.get('customer_contacts')!.push(`persons:${pid}`); }
  if (goal('appointments')) for (const t of a.booked) units.get('appointments')!.push(`tasks:${t.id}`);
  return snap.goals.map((g) => {
    const u = g.status === 'active' ? units.get(g.type) ?? [] : [];
    return { type: g.type, target: g.target, status: g.status, done: u.length, pct: g.target > 0 ? Math.round((u.length / g.target) * 100) : null, units: u };
  });
}

// --- inputs ------------------------------------------------------------------------------------------------------
export interface GoalInputs {
  day: Day; workingDay: boolean;
  /** the window counted (working hours, from «now» when computed during the day) */
  from: string | null; to: string | null;
  workMin: number; busyMin: number; freeMin: number; usableMin: number; utilisation: number;
  overdueIds: string[]; dueTodayContacts: number; renewalPersonIds: string[]; clients: number;
  queue: { total: number; fresh: number };
  pace: { perHour: number; source: 'history' | 'default'; days: number };
  history: { from: Day; to: Day; dials: number; meetings: number; meetingRate: number | null; snapshots: number; completion: Partial<Record<DailyGoalType, number>> };
  week: { goal: number; booked: number; remainingDays: number };
}
const DEFAULT_PACE = 10;
const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); const n = s.length; return n ? (n % 2 ? s[(n - 1) / 2]! : (s[n / 2 - 1]! + s[n / 2]!) / 2) : 0; };

/** the dials-per-hour pace on calling days of the 4 weeks before `day` (≥ 5 dials a day, ≥ 3 such days), else the default */
export function callPace(v: WorkspaceView, day: Day): GoalInputs['pace'] {
  const from = addDays(day, -28);
  const byDay = new Map<string, CallLog[]>();
  for (const l of v.rows('call_logs')) if (l.day >= from && l.day < day) byDay.set(l.day, [...(byDay.get(l.day) ?? []), l]);
  const rates: number[] = [];
  for (const logs of byDay.values()) {
    if (logs.length < 5) continue;
    const ts = logs.map((l) => Date.parse(l.at)).filter(Number.isFinite).sort((a, b) => a - b);
    if (ts.length < 5) continue;
    const hours = Math.max(0.25, (ts[ts.length - 1]! - ts[0]!) / 3_600_000 + 1 / 12);
    rates.push(logs.length / hours);
  }
  if (rates.length < 3) return { perHour: DEFAULT_PACE, source: 'default', days: rates.length };
  return { perHour: Math.max(4, Math.min(40, Math.round(median(rates)))), source: 'history', days: rates.length };
}

export function goalInputs(v: WorkspaceView, day: Day, opts: { now?: string; force?: boolean } = {}): GoalInputs {
  const prefs = goalPrefs(v);
  let w: Interval | null = workingWindow(v, day);
  const workingDay = !!w;
  if (!w && opts.force) { const g = plannerSettings(v); w = { start: toMin(g.from), end: toMin(g.to) }; }
  let start = w?.start ?? 0;
  if (w && opts.now && localDayOf(opts.now) === day) { const n = toMin(localTimeOf(opts.now)); if (Number.isFinite(n)) start = Math.max(start, Math.ceil(n / 15) * 15); }
  const end = w?.end ?? 0;
  const workMin = w ? Math.max(0, end - start) : 0;
  const busyMin = w && workMin ? minutesWithin(busyIntervals(plannerItems(v, day), { exceptGoals: true }), start, end) : 0;
  const freeMin = Math.max(0, workMin - busyMin);
  const tasks = v.rows('tasks');
  const overdueIds = tasks.filter((t) => t.status === 'open' && !!t.dueDate && t.dueDate < day && OVERDUE_KINDS.has(t.kind)).sort((a, b) => a.dueDate!.localeCompare(b.dueDate!) || a.id.localeCompare(b.id)).map((t) => t.id);
  const dueTodayContacts = tasks.filter((t) => t.status === 'open' && t.dueDate === day && ['call', 'followup', 'review'].includes(t.kind) && isClient(v.get('persons', t.personId))).length;
  // renewals in the window whose client was not contacted in the last 14 days
  const since = addDays(day, -14);
  const act = activityByDay(v, addDays(day, -28), addDays(day, -1));
  const recent = new Set<string>();
  for (const [d, a] of act) if (d >= since) for (const pid of personsContacted(v, a).keys()) recent.add(pid);
  const renewalPersonIds = [...new Set(renewalQueue(v, day).filter((r) => r.days >= -14 && r.days <= 30).map((r) => r.policy.personId))].filter((pid) => !recent.has(pid)).sort();
  const keys = doNotCallKeys(v);
  let total = 0, fresh = 0;
  for (const l of v.rows('prospect_lists')) { if (l.archived) continue; for (const q of callQueue(v, l.id, day, { keys })) { total++; if (q.reason === 'new') fresh++; } }
  // the last 4 weeks
  const hFrom = addDays(day, -28), hTo = addDays(day, -1);
  let dials = 0, meetings = 0;
  for (const a of act.values()) for (const d of a.dials) if (prospectingDial(v, d)) { dials++; if (d.outcome === 'meeting') meetings++; }
  const sums = new Map<DailyGoalType, number[]>();
  const snaps = v.rows('daily_goals').filter((s) => s.day >= hFrom && s.day <= hTo);
  for (const s of snaps) for (const p of goalProgress(v, s, act.get(s.day) ?? emptyActivity())) if (p.status === 'active' && p.target > 0) sums.set(p.type, [...(sums.get(p.type) ?? []), Math.min(1.5, p.done / p.target)]);
  const completion: Partial<Record<DailyGoalType, number>> = {};
  for (const [t, xs] of sums) if (xs.length >= 3) completion[t] = Math.round((xs.reduce((s, x) => s + x, 0) / xs.length) * 100) / 100;
  // the week's meetings goal
  const office = getOffice(v);
  const wk = startOfWeek(day, 1);
  let booked = 0;
  for (const [d, a] of act) if (d >= wk) booked += a.booked.length;
  let remainingDays = 0;
  for (let d = day; d <= addDays(wk, 6); d = addDays(d, 1)) if (office.days.includes(weekday(d) || 7)) remainingDays++;
  return {
    day, workingDay, from: w ? fromMin(start) : null, to: w ? fromMin(end) : null, workMin, busyMin, freeMin, usableMin: Math.round(freeMin * prefs.utilisation), utilisation: prefs.utilisation,
    overdueIds, dueTodayContacts, renewalPersonIds, clients: v.rows('persons').filter(isClient).length,
    queue: { total, fresh }, pace: callPace(v, day),
    history: { from: hFrom, to: hTo, dials, meetings, meetingRate: dials >= 30 ? Math.round((meetings / dials) * 1000) / 10 : null, snapshots: snaps.length, completion },
    week: { goal: callGoals(v).meetingsPerWeek, booked, remainingDays: Math.max(1, remainingDays) },
  };
}

// --- the computation (pure) --------------------------------------------------------------------------------------
const hours = (min: number) => Math.round((min / 60) * 2) / 2;
const R = (key: string, params: Record<string, string | number> = {}): GoalReason => ({ key, params });
/** the share of a goal reached lately → a factor and its reason */
function performance(inp: GoalInputs, type: DailyGoalType): { f: number; reason: GoalReason | null } {
  const r = inp.history.completion[type];
  if (r == null) return { f: 1, reason: null };
  const rate = Math.round(r * 100);
  if (r < 0.5) return { f: 0.75, reason: R('performance_low', { rate }) };
  if (r < 0.8) return { f: 0.9, reason: R('performance_low', { rate }) };
  if (r >= 1) return { f: 1.1, reason: R('performance_high', { rate }) };
  return { f: 1, reason: null };
}

/** the goals for the inputs and preferences — deterministic; light days ask more, full days less */
export function computeGoals(inp: GoalInputs, prefs: GoalPrefs, opts: { dialsPerDay?: number } = {}): DailyGoal[] {
  if (!inp.workingDay && !inp.workMin) return [];
  const out: DailyGoal[] = [];
  let budget = inp.usableMin;
  const load = inp.workMin ? inp.freeMin / inp.workMin : 0;
  const dayReason = inp.workMin >= 120 && load >= 0.75 ? R('light_day', { hours: hours(inp.freeMin) }) : inp.workMin >= 120 && load <= 0.35 ? R('full_day', { hours: hours(inp.freeMin) }) : null;
  const push = (type: DailyGoalType, n: number, reasons: (GoalReason | null)[], refs: string[] = [], minutesEach = GOAL_MINUTES[type]) => {
    if (!prefs.enabled[type]) return 0;
    let target = Math.max(0, Math.min(GOAL_LIMITS[type], Math.round(n)));
    const rs = reasons.filter((x): x is GoalReason => !!x);
    const min = prefs.min[type], max = prefs.max[type];
    if (min != null && target < min) { target = min; rs.push(R('pref_min', { value: min })); }
    if (max != null && target > max) { target = max; rs.push(R('pref_max', { value: max })); }
    if (target <= 0) return 0;
    out.push({ type, target, suggested: target, status: 'active', adjusted: false, reasons: rs, refs, minutesEach });
    return target;
  };
  // 1 overdue follow-ups: they are owed — at least one even on a full day
  const nOver = inp.overdueIds.length;
  if (nOver && prefs.enabled.overdue_followups) {
    const fit = Math.floor(budget / GOAL_MINUTES.overdue_followups);
    const t = push('overdue_followups', Math.min(nOver, Math.max(1, fit)), [R('overdue', { count: nOver }), fit < nOver ? R('time_limited', { hours: hours(inp.freeMin) }) : null], inp.overdueIds);
    budget = Math.max(0, budget - t * GOAL_MINUTES.overdue_followups);
  }
  // 2 renewals to contact
  const nRen = inp.renewalPersonIds.length;
  if (nRen && prefs.enabled.renewals) {
    const fit = Math.floor(budget / GOAL_MINUTES.renewals);
    const t = push('renewals', Math.min(nRen, Math.max(1, fit)), [R('renewals', { count: nRen, days: 30 }), fit < nRen ? R('time_limited', { hours: hours(inp.freeMin) }) : null], inp.renewalPersonIds);
    budget = Math.max(0, budget - t * GOAL_MINUTES.renewals);
  }
  // 3 client contacts: what is due today, or ~40 % of the remaining time
  if (inp.clients > 0 && prefs.enabled.customer_contacts) {
    const perf = performance(inp, 'customer_contacts');
    const byTime = Math.floor((budget * 0.4) / GOAL_MINUTES.customer_contacts);
    let n = Math.round(Math.max(inp.dueTodayContacts, byTime) * perf.f);
    n = Math.min(n, inp.clients, Math.max(Math.min(inp.dueTodayContacts, 1), Math.floor(budget / GOAL_MINUTES.customer_contacts)));
    const t = push('customer_contacts', n, [inp.dueTodayContacts ? R('contacts_due', { count: inp.dueTodayContacts }) : null, byTime ? R('contacts_time', { minutes: byTime * GOAL_MINUTES.customer_contacts }) : null, dayReason, perf.reason]);
    budget = Math.max(0, budget - t * GOAL_MINUTES.customer_contacts);
  }
  // 4 prospecting calls: the rest of the planned time at the advisor's pace, never more than the lists hold
  let calls = 0;
  if (inp.queue.total > 0 && prefs.enabled.prospecting_calls) {
    const perf = performance(inp, 'prospecting_calls');
    const h = budget / 60;
    let n = Math.round(inp.pace.perHour * h * perf.f);
    const cap = Math.round((opts.dialsPerDay ?? 40) * 1.5);
    const limited = n > inp.queue.total;
    n = Math.min(n, inp.queue.total, cap);
    calls = push('prospecting_calls', n, [
      R(inp.pace.source === 'history' ? 'calls_time' : 'calls_time_default', { hours: hours(budget), pace: inp.pace.perHour, count: n }),
      R('queue', { count: inp.queue.total, fresh: inp.queue.fresh }), limited ? R('queue_limited', { count: inp.queue.total }) : null, dayReason, perf.reason,
    ], [], Math.round((60 / inp.pace.perHour) * 10) / 10);
  }
  // 5 appointments to book: what the week's meetings goal still needs, spread over the working days left
  if (inp.week.goal > 0 && prefs.enabled.appointments) {
    const need = Math.max(0, inp.week.goal - inp.week.booked);
    if (need > 0) {
      const n = Math.ceil(need / inp.week.remainingDays);
      const expected = inp.history.meetingRate != null && calls ? Math.round((calls * inp.history.meetingRate) / 100 * 10) / 10 : null;
      push('appointments', n, [R('week_meetings', { goal: inp.week.goal, booked: inp.week.booked, need, days: inp.week.remainingDays }), expected != null ? R('expected_from_calls', { count: expected, rate: inp.history.meetingRate! }) : null]);
    }
  }
  return GOAL_DISPLAY_ORDER.map((t) => out.find((g) => g.type === t)).filter((g): g is DailyGoal => !!g);
}

// --- the stored snapshot -----------------------------------------------------------------------------------------
/** one snapshot per day (the same id on every device, so two devices never create two) */
export const dailyGoalsId = (day: Day) => stableId('daily_goals', day);
export function dailyGoals(v: WorkspaceView, day: Day): DailyGoalSnapshot | undefined {
  return v.get('daily_goals', dailyGoalsId(day)) ?? v.where('daily_goals', 'day', day)[0];
}
function compute(v: WorkspaceView, day: Day, ctx: Ctx, force?: boolean) {
  const inputs = goalInputs(v, day, { now: day === ctx.today ? ctx.now : undefined, force });
  return { inputs, goals: computeGoals(inputs, goalPrefs(v), { dialsPerDay: callGoals(v).dialsPerDay }) };
}
/** the day's goals: computed and stored once (the first time the day is opened); nothing changes if they exist */
export function ensureDailyGoals(v: WorkspaceView, day: Day, ctx: Ctx, opts: { force?: boolean } = {}): Changeset {
  if (!isDay(day)) throw new DomainError('goals.day');
  if (dailyGoals(v, day)) return { action: 'goals.keep', puts: [], deletes: [], noUndo: true };
  const { inputs, goals } = compute(v, day, ctx, opts.force);
  if (!inputs.workingDay && !opts.force) return { action: 'goals.day_off', puts: [], deletes: [], noUndo: true };
  const row = created<'daily_goals'>(ctx, { id: dailyGoalsId(day), day, computedAt: ctx.now, goals, inputs, dismissed: [], market: null });
  return { action: 'goals.compute', entity: 'daily_goals', entityId: row.id, summary: day, puts: [put('daily_goals', row)], deletes: [], noUndo: true };
}
/** compute again (after the day changed): the advisor's numbers and skips stay; overdue / renewal lists only grow */
export function recomputeDailyGoals(v: WorkspaceView, day: Day, ctx: Ctx, opts: { force?: boolean } = {}): Changeset {
  const cur = dailyGoals(v, day);
  if (!cur) return ensureDailyGoals(v, day, ctx, { force: true, ...opts });
  const { inputs, goals } = compute(v, day, ctx, true);
  const merged = goals.map((g) => {
    const old = cur.goals.find((x) => x.type === g.type);
    if (!old) return g;
    const refs = [...new Set([...old.refs, ...g.refs])];
    return { ...g, refs, status: old.status, adjusted: old.adjusted, target: old.adjusted ? old.target : g.target };
  });
  // a goal the advisor changed or skipped stays even when the new computation has none of it
  for (const old of cur.goals) if ((old.adjusted || old.status === 'skipped') && !merged.some((g) => g.type === old.type)) merged.push(old);
  const ordered = GOAL_DISPLAY_ORDER.map((t) => merged.find((g) => g.type === t)).filter((g): g is DailyGoal => !!g);
  const row = touched(ctx, cur, { goals: ordered, inputs, computedAt: ctx.now });
  return { action: 'goals.recompute', entity: 'daily_goals', entityId: cur.id, summary: day, puts: [put('daily_goals', row)], deletes: [] };
}
function withGoal(v: WorkspaceView, day: Day, type: DailyGoalType, ctx: Ctx, f: (g: DailyGoal) => DailyGoal, action: string): Changeset {
  const cur = dailyGoals(v, day);
  if (!cur) throw new DomainError('goals.not_found');
  if (!(DAILY_GOAL_TYPES as readonly string[]).includes(type)) throw new DomainError('goals.type');
  let goals = cur.goals;
  if (!goals.some((g) => g.type === type)) goals = [...goals, { type, target: 0, suggested: 0, status: 'active', adjusted: false, reasons: [], refs: [], minutesEach: GOAL_MINUTES[type] }];
  goals = goals.map((g) => (g.type === type ? f(g) : g));
  goals = GOAL_DISPLAY_ORDER.map((t) => goals.find((g) => g.type === t)).filter((g): g is DailyGoal => !!g);
  return { action, entity: 'daily_goals', entityId: cur.id, summary: `${day} · ${type}`, puts: [put('daily_goals', touched(ctx, cur, { goals }))], deletes: [] };
}
/** the advisor's own number for a goal (0 … the type's limit) */
export function adjustDailyGoal(v: WorkspaceView, day: Day, type: DailyGoalType, target: number, ctx: Ctx): Changeset {
  const n = Math.round(Number(target));
  if (!Number.isFinite(n) || n < 0 || n > GOAL_LIMITS[type]) throw new DomainError('goals.target');
  return withGoal(v, day, type, ctx, (g) => ({ ...g, target: n, adjusted: n !== g.suggested, status: 'active' }), 'goals.adjust');
}
/** skip a goal for this day only (or take it back) */
export function setDailyGoalStatus(v: WorkspaceView, day: Day, type: DailyGoalType, status: DailyGoalStatus, ctx: Ctx): Changeset {
  return withGoal(v, day, type, ctx, (g) => ({ ...g, status }), status === 'skipped' ? 'goals.skip' : 'goals.resume');
}
/** a market line for the day: ONLY what the advisor adds, with its source (null removes it) */
export function setMarketNote(v: WorkspaceView, day: Day, note: { text: string; source: string; url?: string | null } | null, ctx: Ctx): Changeset {
  const cur = dailyGoals(v, day);
  if (!cur) throw new DomainError('goals.not_found');
  let market: DailyMarketNote | null = null;
  if (note) {
    const text = clean(note.text).slice(0, 300), source = clean(note.source).slice(0, 200), url = clean(note.url);
    if (!text) throw new DomainError('goals.market_text');
    if (!source) throw new DomainError('goals.market_source');
    if (url && !/^https?:\/\/\S+$/i.test(url)) throw new DomainError('goals.market_url');
    market = { text, source, url: url || null, addedAt: ctx.now };
  }
  return { action: 'goals.market', entity: 'daily_goals', entityId: cur.id, summary: day, puts: [put('daily_goals', touched(ctx, cur, { market }))], deletes: [] };
}

// --- planner suggestions -----------------------------------------------------------------------------------------
const SUGGEST_ORDER: readonly DailyGoalType[] = ['prospecting_calls', 'overdue_followups', 'renewals', 'customer_contacts'];
export interface PlanSuggestion extends PlanItem { goalType: DailyGoalType; count: number }
/** blocks proposed for the goals still open, placed only in FREE time (never over an appointment or another block) */
export function planSuggestions(v: WorkspaceView, day: Day, opts: { now?: string; items?: PlanItem[] } = {}): PlanSuggestion[] {
  const snap = dailyGoals(v, day);
  if (!snap) return [];
  const items = opts.items ?? plannerItems(v, day);
  const slot = plannerSettings(v).slotMin;
  let notBefore: number | undefined;
  if (opts.now && localDayOf(opts.now) === day) { const n = toMin(localTimeOf(opts.now)); if (Number.isFinite(n)) notBefore = Math.ceil(n / slot) * slot; }
  let free = freeSlots(v, day, items, { notBefore, minMinutes: 30 });
  const prog = goalProgress(v, snap);
  const blocks = v.where('plan_blocks', 'day', day);
  const out: PlanSuggestion[] = [];
  for (const type of SUGGEST_ORDER) {
    const g = snap.goals.find((x) => x.type === type && x.status === 'active');
    if (!g) continue;
    const left = g.target - (prog.find((p) => p.type === type)?.done ?? 0);
    if (left <= 0 || (snap.dismissed ?? []).includes(`sug:${type}`)) continue;
    if (blocks.some((b) => b.goalType === type && b.status !== 'cancelled')) continue;
    const want = Math.min(120, Math.max(30, Math.ceil((left * g.minutesEach) / slot) * slot));
    const aligned = free.map((f) => ({ start: Math.ceil(f.start / slot) * slot, end: f.end })).filter((f) => f.end - f.start >= 30);
    const fit = aligned.find((f) => f.end - f.start >= want) ?? [...aligned].sort((a, b) => (b.end - b.start) - (a.end - a.start))[0];
    if (!fit) break;
    const len = Math.min(want, Math.floor((fit.end - fit.start) / slot) * slot);
    if (len < 30) continue;
    const s = fit.start, e = fit.start + len;
    out.push({ key: `sug:${type}`, source: 'suggestion', kind: 'goal', start: fromMin(s), end: fromMin(e), title: '', status: 'planned', goalType: type, count: left, type: planTypeOf({ source: 'suggestion', kind: 'goal', goalType: type }), committed: false, movable: false, conflictWith: [] });
    free = free.flatMap((f) => (e <= f.start || s >= f.end ? [f] : [{ start: f.start, end: s }, { start: e, end: f.end }])).filter((f) => f.end - f.start >= 30);
  }
  return out;
}
/** accepting a suggestion makes it a planned block of the advisor (kind «goal»); accepting again re-plans the same block */
export function acceptPlanSuggestion(v: WorkspaceView, day: Day, s: { goalType: DailyGoalType; start: string; end: string }, title: string, ctx: Ctx): Changeset {
  if (!(DAILY_GOAL_TYPES as readonly string[]).includes(s.goalType)) throw new DomainError('goals.type');
  const type = planTypeOf({ source: 'block', kind: 'goal', goalType: s.goalType });
  const cs = savePlanBlock(v, { id: stableId('plan_suggestion', `${day}:${s.goalType}`), day, start: s.start, end: s.end, kind: 'goal', title, goalType: s.goalType, source: 'suggestion', activity: type === 'calls' || type === 'followup' ? type : null }, ctx);
  return { ...cs, action: 'plan.suggestion_accept' };
}
/** accept every open suggestion of the day at once («add the goals to the plan») */
export function acceptAllPlanSuggestions(v: WorkspaceView, day: Day, sugg: readonly PlanSuggestion[], title: (s: PlanSuggestion) => string, ctx: Ctx): Changeset {
  if (!sugg.length) throw new DomainError('plan.no_suggestions');
  const puts = sugg.flatMap((s) => acceptPlanSuggestion(v, day, s, title(s), ctx).puts);
  return { action: 'plan.suggestion_accept_all', entity: 'plan_blocks', summary: `${day} · ${sugg.length}`, puts, deletes: [] };
}
/** «not today» for a suggestion */
export function dismissPlanSuggestion(v: WorkspaceView, day: Day, key: string, ctx: Ctx): Changeset {
  const cur = dailyGoals(v, day);
  if (!cur) throw new DomainError('goals.not_found');
  const dismissed = [...new Set([...(cur.dismissed ?? []), key])];
  return { action: 'plan.suggestion_dismiss', entity: 'daily_goals', entityId: cur.id, summary: key, puts: [put('daily_goals', touched(ctx, cur, { dismissed }))], deletes: [] };
}

// --- for the daily review (#25) ----------------------------------------------------------------------------------
/** the day's goals as outcomes (null when no goals were set that day): what was set, what the recorded activity reached
 *  (each activity once, see goalProgress), skipped / adjusted flags. Registered below as #25's goal source. */
export interface DailyGoalOutcome { type: DailyGoalType; labelKey: string; target: number; suggested: number; achieved: number; skipped: boolean; adjusted: boolean; source: 'system'; units: string[] }
export function dailyGoalOutcomes(v: WorkspaceView, day: Day): DailyGoalOutcome[] | null {
  const snap = dailyGoals(v, day);
  if (!snap) return null;
  const prog = goalProgress(v, snap);
  return snap.goals.map((g) => {
    const p = prog.find((x) => x.type === g.type);
    return { type: g.type, labelKey: `goals.type.${g.type}`, target: g.target, suggested: g.suggested, achieved: p?.done ?? 0, skipped: g.status === 'skipped', adjusted: g.adjusted, source: 'system', units: p?.units ?? [] };
  });
}

/** #25's end-of-day report reads the goals through performance.ts: the active goals of the day (skipped ones are not
 *  counted), labelled with their i18n key (the report screen translates it), met = reached by recorded activity */
export function dailyGoalsAsReportGoals(v: WorkspaceView, day: Day): GoalOutcome[] | null {
  const o = dailyGoalOutcomes(v, day)?.filter((g) => !g.skipped && g.target > 0);
  return o && o.length ? o.map((g) => ({ key: g.type, label: g.labelKey, target: g.target, achieved: g.achieved, met: g.achieved >= g.target })) : null;
}
setGoalSource(dailyGoalsAsReportGoals);
